import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicsCard from '@/components/feature/ComicsCard';
import { mangaList, mangaCategories } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const manga = mangaList.find((m) => m.id === id);

  if (!manga) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center text-foreground-200">
              <i className="ri-error-warning-line text-5xl"></i>
            </div>
            <p className="text-foreground-500 mt-4 text-lg">漫画未找到</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-6 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = mangaCategories.find((c) => c.slug === manga.categorySlug);
  const relatedManga = mangaList
    .filter((m) => m.categorySlug === manga.categorySlug && m.id !== manga.id)
    .slice(0, 4);

  const otherManga = mangaList
    .filter((m) => m.id !== manga.id && m.categorySlug !== manga.categorySlug)
    .slice(0, 4);

  return (
    <>
      <SEOHead
        title={`${manga.title} - 韩国漫画网_韩漫在线阅读`}
        description={`${manga.title}，${manga.author}作品，${manga.category}类韩漫。${manga.description.slice(0, 120)}。韩国漫画网提供无删减下拉式韩漫免费在线阅读。`}
        keywords={`${manga.title},${manga.category}韩漫,韩漫在线阅读,免费韩国漫画`}
        canonicalUrl={`https://koreanmanga.example.com/article/${manga.id}`}
        ogType="article"
        schemaData={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: manga.title,
            description: manga.description.slice(0, 160),
            author: {
              '@type': 'Person',
              name: manga.author,
            },
            datePublished: manga.chapters[0]?.date || '2024-01-01',
            dateModified: manga.chapters[manga.chapters.length - 1]?.date || '2024-08-01',
          },
        ]}
      />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: `${manga.category}漫画`, path: `/category/${manga.categorySlug}` },
              { name: manga.title },
            ]}
          />
        </div>

        <article className="max-w-7xl mx-auto px-4 md:px-6 pb-20">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-12">
            <div className="flex-shrink-0 w-full sm:w-[280px] lg:w-[320px]">
              <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                <img
                  src={manga.cover}
                  alt={manga.title}
                  title={`${manga.title} - 韩国漫画网韩漫在线阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="mt-6 space-y-4">
                <Link
                  to={`/article/${manga.id}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="block w-full text-center py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  开始阅读
                </Link>
                <button className="w-full text-center py-3 bg-background-100 text-foreground-700 rounded-full font-medium text-sm hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer">
                  <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                    <i className="ri-bookmark-line"></i>
                  </span>
                  加入收藏
                </button>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900">
                {manga.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-foreground-500">
                <span>作者：{manga.author}</span>
                <span className="text-foreground-300">|</span>
                <span>分类：{manga.category}</span>
                <span className="text-foreground-300">|</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                    manga.status === '连载中'
                      ? 'bg-accent-100 text-accent-800'
                      : 'bg-secondary-100 text-secondary-800'
                  }`}
                >
                  {manga.status}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
                <span className="flex items-center gap-1 text-accent-500 font-semibold whitespace-nowrap">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill"></i>
                  </span>
                  {manga.rating} 分
                </span>
                <span className="text-foreground-400 whitespace-nowrap">
                  <span className="w-4 h-4 inline-flex items-center justify-center mr-0.5">
                    <i className="ri-eye-line"></i>
                  </span>
                  {manga.views >= 10000
                    ? `${(manga.views / 10000).toFixed(0)}万`
                    : manga.views}{' '}
                  阅读
                </span>
                <span className="text-foreground-400 whitespace-nowrap">
                  <span className="w-4 h-4 inline-flex items-center justify-center mr-0.5">
                    <i className="ri-file-list-3-line"></i>
                  </span>
                  共 {manga.chapterCount} 话
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {manga.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs font-medium whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="mt-6">
                <h2 className="font-heading font-semibold text-base text-foreground-900 mb-3">
                  作品简介
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {manga.description}
                </p>
              </div>

              <div className="mt-8">
                <h2 className="font-heading font-semibold text-base text-foreground-900 mb-4">
                  章节列表
                </h2>
                <div className="bg-background-100 rounded-xl p-5 max-h-[500px] overflow-y-auto">
                  <div className="space-y-1">
                    {manga.chapters.map((chapter) => (
                      <Link
                        key={chapter.id}
                        to={`/article/${manga.id}`}
                        className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-background-50 transition-colors group"
                      >
                        <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors">
                          {chapter.title}
                        </span>
                        <span className="text-xs text-foreground-400 whitespace-nowrap">
                          {chapter.date}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {relatedManga.length > 0 && (
            <section className="mt-16" aria-labelledby="related-heading">
              <h2
                id="related-heading"
                className="font-heading font-bold text-xl text-foreground-900 mb-6"
              >
                同类型推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                {relatedManga.map((m) => (
                  <ComicsCard key={`related-${m.id}`} manga={m} />
                ))}
              </div>
            </section>
          )}

          {otherManga.length > 0 && (
            <section className="mt-12" aria-labelledby="more-heading">
              <h2
                id="more-heading"
                className="font-heading font-bold text-xl text-foreground-900 mb-6"
              >
                猜你喜欢
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                {otherManga.map((m) => (
                  <ComicsCard key={`more-${m.id}`} manga={m} />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}